import { useEffect } from 'react';
import { extractErrorMessage } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { FileItem, FileStatus } from '@sib/types';
import { runWithConcurrencyPool } from './concurrency-pool';
import { resolveDocumentUploadPoolSize } from './document-upload-concurrency';
import {
  prefetchDocumentMd5Hash,
  syncDocumentMd5HashPrefetch,
} from './document-upload-md5';
import { FileUploadStore } from './file-upload-store';

interface UseDocumentFileMd5PrefetchProps {
  enabled: boolean;
  files: FileItem[];
  getFiles: FileUploadStore['getFiles'];
  updateFile: FileUploadStore['updateFile'];
}

export const useDocumentFileMd5Prefetch = ({
  enabled,
  files,
  getFiles,
  updateFile,
}: UseDocumentFileMd5PrefetchProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!enabled) return;

    syncDocumentMd5HashPrefetch(files.map(file => file.id));

    let cancelled = false;

    const prefetchPendingFiles = async () => {
      const pendingFiles = files.filter(
        fileItem => fileItem.status === FileStatus.Pending && !fileItem.md5Hash
      );

      await runWithConcurrencyPool(
        pendingFiles,
        () => resolveDocumentUploadPoolSize('md5'),
        async fileItem => {
          if (cancelled) return;

          try {
            await prefetchDocumentMd5Hash(
              fileItem,
              (item, md5Hash) => {
                const currentFile = getFiles().find(
                  file => file.id === item.id
                );

                if (!currentFile || currentFile.md5Hash) return;

                updateFile({ ...currentFile, md5Hash });
              },
              (item, error) => {
                const currentFile = getFiles().find(
                  file => file.id === item.id
                );

                if (!currentFile) return;

                updateFile({
                  ...currentFile,
                  status: FileStatus.Error,
                  errorMessage: extractErrorMessage(
                    error,
                    t('fileManager.uploadFailedMessage')
                  ),
                });
              }
            );
          } catch {
            // Error state is handled in prefetchDocumentMd5Hash onError.
          }
        }
      );
    };

    prefetchPendingFiles();

    return () => {
      cancelled = true;
    };
  }, [enabled, files, getFiles, t, updateFile]);
};
